import type { AccidentPhotoRow, AccidentReportRow, TruckRow } from '@routeback/core';
import { useToast } from '@routeback/ui';
import { useState } from 'react';
import { usePeople } from '../../lib/people';
import { fileBytes } from '../../lib/photos';
import { downloadAccident, fileSafe } from '../../lib/pdf/make';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link, useParams } from 'react-router-dom';
import { useApp } from '../../lib/app';
import { todayIn } from '../timeoff/timeoff';
import { summarySections, withDefaults, type AccidentData } from './accident';
import { ReportSummary } from './ReportSummary';
import { MarkReviewed } from '../formsadmin/FormsAdmin';

export const ACCIDENT_STATUS = { draft: 'Draft — not sent', submitted: 'Sent', reviewed: 'Reviewed by manager' } as const;

/** A sent accident report, read-only. */
export function AccidentDetailView() {
  const { id } = useParams();
  const app = useApp();
  const r = useLiveQuery(() => app.db.table<AccidentReportRow, string>('accident_reports').get(id ?? ''), [app.db, id]);
  const photos = useLiveQuery(
    async () => (await app.db.table<AccidentPhotoRow, string>('accident_photos').where('company_id').equals(app.company.id).toArray()).filter((p) => !p.deleted_at && p.report_id === id),
    [app.db, app.company.id, id], [] as AccidentPhotoRow[],
  );
  if (r === undefined) return <div className="spinner" />;
  if (!r || r.deleted_at) return <div className="card">Not found.</div>;
  const d = withDefaults(r.data, todayIn(app.details.timezone), '');
  return (
    <div>
      <div className="settings-head"><Link to={app.isManager && r.user_id !== app.userId ? '/manage/accidents' : '/requests'} className="btn btn-sm btn-ghost">‹ {app.isManager && r.user_id !== app.userId ? 'Accident reports' : 'Requests'}</Link><h2>🚨 Accident report</h2></div>
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong>{r.location || 'Accident'}</strong>
          <span className={'chip' + (r.status !== 'draft' ? ' on' : '')}>{ACCIDENT_STATUS[r.status]}</span>
        </div>
        {r.submitted_at && <p className="muted small">Sent {new Date(r.submitted_at).toLocaleString()}</p>}
        <div className="row row-wrap" style={{ gap: 8, marginBottom: 10 }}><MarkReviewed r={r} />{r.status !== 'draft' && <PdfButton r={r} d={d} photos={photos} />}</div>
        {r.status === 'draft' && <Link to="/accident" className="btn btn-primary" style={{ marginBottom: 10 }}>Continue the report</Link>}
        <ReportSummary report={r} data={d} photos={photos} />
      </div>
    </div>
  );
}

const CAT: Record<AccidentPhotoRow['category'], string> = { my_vehicle: 'Your vehicle', other_vehicle: 'Other vehicle', scene: 'Scene', other: 'Other' };

/** The whole report (photos included) as a PDF to print, save or send to insurance. */
function PdfButton({ r, d, photos }: { r: AccidentReportRow; d: AccidentData; photos: AccidentPhotoRow[] }) {
  const app = useApp();
  const toast = useToast();
  const { names } = usePeople();
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try {
      const truck = r.truck_id ? await app.db.table<TruckRow, string>('trucks').get(r.truck_id) : undefined;
      const driver = r.user_id === app.userId ? app.userName : r.user_id ? names.get(r.user_id) ?? 'Driver' : '';
      const when = d.date ? new Date(`${d.date}T${d.time || '12:00'}`).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', ...(d.time ? { hour: 'numeric', minute: '2-digit' } : {}) }) : '';
      const pics: { label: string; bytes: Uint8Array; png: boolean }[] = [];
      for (const p of photos) {
        const bytes = await fileBytes(p.file_path, 'photos');
        if (bytes) pics.push({ label: CAT[p.category] + (p.caption ? ` — ${p.caption}` : ''), bytes, png: /\.png$/i.test(p.file_path) });
      }
      const how = await downloadAccident({
        company: app.details.name, title: 'Accident report', subtitle: [driver, truck ? `unit ${truck.unit_number}` : '', when].filter(Boolean).join(' · '),
        sections: summarySections(d, driver, truck?.unit_number ?? '', when), photos: pics,
      }, `Accident_${d.date || 'report'}_${fileSafe(truck?.unit_number ?? driver)}.pdf`);
      if (how === 'downloaded') toast('Saved to your downloads');
      if (pics.length < photos.length) toast(`${photos.length - pics.length} photo(s) couldn’t be loaded — check your signal`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Couldn’t make the PDF');
    } finally {
      setBusy(false);
    }
  };
  return <button type="button" className="btn" disabled={busy} onClick={() => void make()}>{busy ? 'Making the PDF…' : '📄 PDF'}</button>;
}
